import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";
import { AuthProvider, useAuth } from "./lib/auth";
import { attachAutoSync } from "./lib/sync";

import Login from "./pages/Login";
import ScanRedirect from "./pages/ScanRedirect";

import SuperLayout from "./pages/super/SuperLayout";
import SuperDashboard from "./pages/super/SuperDashboard";
import Organizations from "./pages/super/Organizations";
import OrgAdmins from "./pages/super/OrgAdmins";
import Branding from "./pages/super/Branding";

import OrgLayout from "./pages/org/OrgLayout";
import OrgDashboard from "./pages/org/OrgDashboard";
import Members from "./pages/org/Members";
import Events from "./pages/org/Events";
import Scanner from "./pages/org/Scanner";
import AttendanceLogs from "./pages/org/AttendanceLogs";
import Reports from "./pages/org/Reports";

import MemberLayout from "./pages/member/MemberLayout";
import MemberDashboard from "./pages/member/MemberDashboard";
import MemberQR from "./pages/member/MemberQR";
import MemberDocuments from "./pages/member/MemberDocuments";
import MemberProfile from "./pages/member/MemberProfile";

function ProtectedRoute({ roles, children }) {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500" data-testid="auth-loading">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin") return <Navigate to="/super" replace />;
  if (user.role === "org_admin") return <Navigate to="/org" replace />;
  return <Navigate to="/member" replace />;
}

function AppRoutes() {
  useEffect(() => {
    attachAutoSync();
  }, []);

  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/scan/:token" element={<ScanRedirect />} />

      <Route
        path="/super"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <SuperLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<SuperDashboard />} />
        <Route path="organizations" element={<Organizations />} />
        <Route path="org-admins" element={<OrgAdmins />} />
        <Route path="branding" element={<Branding />} />
      </Route>

      <Route
        path="/org"
        element={
          <ProtectedRoute roles={["org_admin"]}>
            <OrgLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<OrgDashboard />} />
        <Route path="members" element={<Members />} />
        <Route path="events" element={<Events />} />
        <Route path="scanner" element={<Scanner />} />
        <Route path="attendance" element={<AttendanceLogs />} />
        <Route path="reports" element={<Reports />} />
      </Route>

      <Route
        path="/member"
        element={
          <ProtectedRoute roles={["member"]}>
            <MemberLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<MemberDashboard />} />
        <Route path="qr" element={<MemberQR />} />
        <Route path="documents" element={<MemberDocuments />} />
        <Route path="profile" element={<MemberProfile />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
